import React, { useState, useEffect, useRef } from 'react';
import { fetchPrayerTimes } from '../../services/apiService';
import { findNextPrayer } from '../../utils/prayerTimeUtils';
import { formatTo12Hour } from '../../utils/timeFormatters';
import { getLocationTimeFromTimezone, getEstimatedLocationTime } from '../../utils/timezoneHelper';

const PrayerTimesPanel = ({
  coordinates,
  onPrayerTimesLoaded,
  showPrayerMarkers,
  setShowPrayerMarkers,
  onNextPrayerUpdate
}) => {
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadedCoordsRef = useRef(null);
  const timerRef = useRef(null);

  // Load prayer times when coordinates change
  useEffect(() => {
    if (!coordinates) return;

    if (loadedCoordsRef.current &&
        loadedCoordsRef.current.lat === coordinates.lat &&
        loadedCoordsRef.current.lng === coordinates.lng) {
      return;
    }

    const loadPrayerTimes = async () => {
      setLoading(true);
      try {
        const times = await fetchPrayerTimes(coordinates.lat, coordinates.lng);
        setPrayerTimes(times);
        loadedCoordsRef.current = { lat: coordinates.lat, lng: coordinates.lng };
        if (times && onPrayerTimesLoaded) {
          onPrayerTimesLoaded(times);
        }
      } catch (error) {
        console.error('Error loading prayer times:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPrayerTimes();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [coordinates, onPrayerTimesLoaded]);

  // Calculate next prayer using shared utility and timezone-aware time
  useEffect(() => {
    if (!prayerTimes || !coordinates) return;

    const updateNextPrayer = () => {
      const locationTime = getLocationTimeFromTimezone(prayerTimes.timezone)
        || getEstimatedLocationTime(coordinates);
      const result = findNextPrayer(prayerTimes, locationTime);
      if (onNextPrayerUpdate) {
        if (result) {
          onNextPrayerUpdate(result.prayer, result.formatted);
        } else {
          onNextPrayerUpdate({ name: 'Prayer' }, 'N/A');
        }
      }
    };

    updateNextPrayer();
    timerRef.current = setInterval(updateNextPrayer, 60 * 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [prayerTimes, coordinates, onNextPrayerUpdate]);

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-white/80 font-medium">Prayer Times</h3>
        {prayerTimes && (
          <button 
            className={`text-xs px-2 py-1 rounded ${showPrayerMarkers ? 'bg-blue-500 text-white' : 'bg-white/20 text-white'}`}
            onClick={() => setShowPrayerMarkers(!showPrayerMarkers)}
          >
            {showPrayerMarkers ? 'Hide Markers' : 'Show on Timeline'}
          </button>
        )}
      </div>
      
      {loading ? (
        <div className="text-center py-2">
          <div className="inline-block w-5 h-5 border-2 border-white/50 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-white/70">Loading prayer times...</span>
        </div>
      ) : prayerTimes ? (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center">
            <span className="text-white/70">Fajr:</span>
          </div>
          <span className="text-white">{formatTo12Hour(prayerTimes?.fajr || 'N/A')}</span>
          
          <div className="flex items-center">
            <span className="text-white/70">Dhuhr:</span>
          </div>
          <span className="text-white">{formatTo12Hour(prayerTimes?.dhuhr || 'N/A')}</span>
          
          <div className="flex items-center">
            <span className="text-white/70">Asr:</span>
          </div>
          <span className="text-white">{formatTo12Hour(prayerTimes?.asr || 'N/A')}</span>
          
          <div className="flex items-center">
            <span className="text-white/70">Maghrib:</span>
          </div>
          <span className="text-white">{formatTo12Hour(prayerTimes?.maghrib || 'N/A')}</span>
          
          <div className="flex items-center">
            <span className="text-white/70">Isha:</span>
          </div>
          <span className="text-white">{formatTo12Hour(prayerTimes?.isha || 'N/A')}</span>
          
          <div className="flex items-center col-span-2 mt-1">
            <span className="text-xs text-white/60">Method: {prayerTimes?.method || 'ISNA'}</span>
          </div>
        </div>
      ) : (
        <div className="text-white/70 text-sm">
          Prayer times not available. Please check your connection.
        </div>
      )}
    </div>
  );
};

export default PrayerTimesPanel;